import { motion } from 'framer-motion'
import type { Diamond } from '../api/types'
import { StoneImage } from './StoneImage'
import { formatPrice, formatPerCarat, formatCarat, displayStockId } from '../lib/format'
import { FavoriteStoneButton, FavoriteSupplierButton } from './FavoriteButtons'
import { AddToSourcingButton } from './sourcing/AddToSourcingButton'
import { supplierReveal } from './StoneActions'

interface Props {
  stone: Diamond
  onOpen?: (s: Diamond) => void
  /** When true, hides the price column. Anonymous visitors see this; signed-in
   *  buyers see real prices. Same guard the mobile app uses. */
  blurPrice?: boolean
}

/**
 * Compact one-line diamond row for inline use in the chat stream.
 * Mirrors the mobile DiamondRow exactly so a buyer who switches
 * between mobile and web sees the same density. The grid Search
 * page uses DiamondCard (vertical card) instead — different surfaces,
 * different shapes, same data.
 *
 * The row is a `role="button"` DIV (not a <button>) so the save / sourcing
 * controls can be real nested buttons — a <button> can't contain a <button>,
 * and the same reason StoneTable's row is a div. Each control stops its press
 * from reaching the row's open handler. The save-supplier heart only shows for
 * a revealed supplier (verified caller); the sourcing "+" only shows when the
 * RFQ surface is armed (flag-gated, inert on prod) — both self-gate to nothing.
 */
export function DiamondRow({ stone, onOpen, blurPrice }: Props) {
  const videoUrl = stone.media?.videoUrl ?? null
  const supplierShown = supplierReveal(stone).state === 'revealed'

  return (
    <motion.div
      role="button"
      tabIndex={0}
      whileTap={{ scale: 0.99 }}
      onClick={() => onOpen?.(stone)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen?.(stone) } }}
      className="group flex w-full cursor-pointer items-center gap-3 rounded-xl border border-border bg-paper px-3 py-2.5 text-left hover:shadow-card transition-shadow"
      data-testid="diamond-row"
      data-stock-id={stone.stockId}
      aria-label={`Diamond ${displayStockId(stone)}`}
    >
      <div className="relative h-12 w-12 shrink-0 overflow-hidden rounded-full ring-1 ring-border bg-gradient-to-br from-cream to-[#eef1f6]">
        <StoneImage stone={stone} badge={false} width={96} />
        {!!videoUrl && (
          <span className="absolute bottom-0.5 right-0.5 inline-flex h-3.5 w-3.5 items-center justify-center rounded-full bg-paper/95 text-[7px] font-bold text-ink">
            360
          </span>
        )}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-1.5">
          <p className="text-[13px] font-semibold text-ink leading-tight truncate">#{displayStockId(stone)}</p>
          {stone.tripleEx && (
            <span className="shrink-0 rounded bg-cream border border-gold/30 px-1 py-px text-[8px] font-bold uppercase tracking-wide text-gold">3EX</span>
          )}
        </div>
        <p className="text-[11.5px] text-muted leading-tight truncate">
          {stone.shape} · {formatCarat(stone.carat)}
          {stone.color ? ` · ${stone.color}` : ''}{stone.clarity ? ` ${stone.clarity}` : ''}
        </p>
      </div>

      <div className="shrink-0 text-right">
        {blurPrice ? (
          <p className="text-[12px] font-semibold text-gold">Sign in</p>
        ) : stone.priceLocked ? (
          // Signed-in but not yet KYC-approved — server withholds prices.
          <p className="text-[11px] font-semibold text-gold">Verifying</p>
        ) : (
          <>
            {/* $/ct leads (Vikram, 2026-07-26); total price as the sub-line. */}
            <p className="text-[14px] font-bold text-ink leading-tight tabular-nums">{formatPerCarat(stone.pricePerCarat)}</p>
            <p className="text-[10.5px] text-soft leading-tight tabular-nums">{formatPrice(stone.priceUSD)} total</p>
          </>
        )}
      </div>

      {/* Save / sourcing controls. Real nested buttons (the row is a div), each
          stopping its press from reaching the open handler. The "+" self-gates
          (renders nothing off-flag); the heart only shows for a revealed
          supplier; the bookmark is always available (401s → sign-in for anon). */}
      <div
        className="flex shrink-0 items-center gap-0.5"
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
      >
        <AddToSourcingButton stone={stone} size={13} />
        {supplierShown && <FavoriteSupplierButton stone={stone} />}
        <FavoriteStoneButton stone={stone} />
      </div>
    </motion.div>
  )
}
